import React, { useState, useEffect } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { toast } from "sonner@2.0.3";
import { Plus, Trash, Eye, Pencil, Save } from "lucide-react";
import {
  createJob,
  getJobsAdmin,
  deleteJob,
  getAdminJobById,
  updateJob,
} from "../../services/Job";
import { withCurrent } from "../../services/SiteSettings";
import {
  useAdminTaxonomy,
  TaxonomyManagerDialog,
  ManageOptionsButton,
  TaxonomySection,
} from "./TaxonomyManager";

// Dropdown options
const EMPLOYMENT_TYPES = ["Full-time", "Part-time", "Contract"];
const STATUS_TYPES = ["ACTIVE", "INACTIVE", "CLOSED"];

export function AddCareerForm() {
  const { taxonomy, saveTaxonomy } = useAdminTaxonomy();
  const [managing, setManaging] = useState<TaxonomySection | null>(null);
  const POSITIONS = taxonomy.consultantPositions || [];
  const POSITION_NAMES = POSITIONS.map((p) => p.name);
  const findPosition = (name?: string) => POSITIONS.find((p) => p.name === name);
  const DEPARTMENTS = taxonomy.thematicAreas.map((a) => a.name);
  const SUB_THEMATIC_AREAS: Record<string, string[]> = Object.fromEntries(
    taxonomy.thematicAreas.map((a) => [a.name, a.subAreas || []])
  );
  const ASSIGNMENT_TYPES = taxonomy.assignmentTypes.map((t) => t.name);
  const SUB_ASSIGNMENT_TYPES: Record<string, string[]> = Object.fromEntries(
    taxonomy.assignmentTypes.map((t) => [t.name, t.subTypes || []])
  );
  const [formData, setFormData] = useState({
    title: "",
    department: "",
    subThematicArea: "",
    assignmentType: "",
    subAssignmentType: "",
    location: "",
    type: "",
    description: "",
    education: "",
    status: "",
    qualifications: [""],
    responsibilities: [""],
  });

  const [jobs, setJobs] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState<any | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const data = await getJobsAdmin();
      setJobs(data?.jobs || data || []);
    } catch (error: any) {
      toast.error(error.message || "Failed to fetch jobs");
    } finally {
      setLoading(false);
    }
  };

  // Check if selected position should show additional fields
  const shouldShowAdditionalFields = () => !!findPosition(formData.title)?.requiresThematicArea;

  // For edit dialog
  const shouldShowAdditionalFieldsEdit = () => !!findPosition(selectedJob?.title)?.requiresThematicArea;

  // Autofill for form
  const handleTitleChange = (value: string) => {
    const autofill = findPosition(value);
    if (autofill) {
      setFormData({
        ...formData,
        title: value,
        education: autofill.education,
        qualifications: autofill.qualifications.length ? autofill.qualifications : [""],
        responsibilities: autofill.responsibilities.length ? autofill.responsibilities : [""],
      });
    } else {
      setFormData({ ...formData, title: value });
    }
  };

  // Handle department change to reset sub-thematic area
  const handleDepartmentChange = (value: string) => {
    setFormData({
      ...formData,
      department: value,
      subThematicArea: "" // Reset sub-thematic area when department changes
    });
  };

  // Handle assignment type change to reset sub-assignment type
  const handleAssignmentTypeChange = (value: string) => {
    setFormData({
      ...formData,
      assignmentType: value,
      subAssignmentType: "" // Reset sub-assignment type when assignment type changes
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        qualifications: formData.qualifications.filter((q) => q.trim() !== ""),
        responsibilities: formData.responsibilities.filter((r) => r.trim() !== ""),
      };
      await createJob(payload);
      toast.success("Career opportunity added successfully!");
      setFormData({
        title: "",
        department: "",
        subThematicArea: "",
        assignmentType: "",
        subAssignmentType: "",
        location: "",
        type: "",
        description: "",
        education: "",
        status: "",
        qualifications: [""],
        responsibilities: [""],
      });
      fetchJobs();
    } catch (error: any) {
      toast.error(error.message || "Failed to add career opportunity");
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Are you sure you want to delete this job?")) return;
    try {
      await deleteJob(id);
      toast.success("Job deleted successfully");
      fetchJobs();
    } catch (error: any) {
      toast.error(error.message || "Failed to delete job");
    }
  };

  const handleView = async (id: string) => {
    try {
      const data = await getAdminJobById(id);
      const job = data?.job || data;
      setSelectedJob({
        ...job,
        qualifications: Array.isArray(job.qualifications)
          ? job.qualifications
          : job.qualifications
            ? [job.qualifications]
            : [""],
        responsibilities: Array.isArray(job.responsibilities)
          ? job.responsibilities
          : job.responsibilities
            ? [job.responsibilities]
            : [""],
      });
      setIsEditing(false);
      setViewDialogOpen(true);
    } catch (error: any) {
      toast.error(error.message || "Failed to fetch job details");
    }
  };

  const handleUpdate = async () => {
    if (!selectedJob) return;
    try {
      const payload = {
        title: selectedJob.title,
        department: selectedJob.department,
        subThematicArea: selectedJob.subThematicArea,
        assignmentType: selectedJob.assignmentType,
        subAssignmentType: selectedJob.subAssignmentType,
        location: selectedJob.location,
        type: selectedJob.type,
        description: selectedJob.description,
        education: selectedJob.education,
        status: selectedJob.status,
        qualifications: Array.isArray(selectedJob.qualifications)
          ? selectedJob.qualifications.filter((q: string) => q.trim() !== "")
          : [],
        responsibilities: Array.isArray(selectedJob.responsibilities)
          ? selectedJob.responsibilities.filter((r: string) => r.trim() !== "")
          : [],
      };
      await updateJob(selectedJob.id, payload);
      toast.success("Job updated successfully!");
      setIsEditing(false);
      fetchJobs();
      setViewDialogOpen(false);
    } catch (error: any) {
      toast.error(error.message || "Failed to update job");
    }
  };

  // Autofill for edit dialog
  const handleJobFieldChange = (field: string, value: string) => {
    if (field === "title") {
      const autofill = findPosition(value);
      if (autofill) {
        setSelectedJob((prev: any) => ({
          ...prev,
          title: value,
          education: autofill.education,
          qualifications: autofill.qualifications.length ? autofill.qualifications : [""],
          responsibilities: autofill.responsibilities.length ? autofill.responsibilities : [""],
        }));
        return;
      }
    }

    // Reset sub-thematic area when department changes in edit mode
    if (field === "department") {
      setSelectedJob((prev: any) => ({
        ...prev,
        department: value,
        subThematicArea: ""
      }));
      return;
    }

    // Reset sub-assignment type when assignment type changes in edit mode
    if (field === "assignmentType") {
      setSelectedJob((prev: any) => ({
        ...prev,
        assignmentType: value,
        subAssignmentType: ""
      }));
      return;
    }

    setSelectedJob((prev: any) => ({ ...prev, [field]: value }));
  };

  const handleArrayFieldChange = (
    field: "qualifications" | "responsibilities",
    index: number,
    value: string
  ) => {
    setSelectedJob((prev: any) => {
      const arr = Array.isArray(prev[field]) ? [...prev[field]] : [""];
      arr[index] = value;
      return { ...prev, [field]: arr };
    });
  };

  const addArrayField = (field: "qualifications" | "responsibilities") => {
    setSelectedJob((prev: any) => ({
      ...prev,
      [field]: Array.isArray(prev[field]) ? [...prev[field], ""] : [""],
    }));
  };

  const removeArrayField = (field: "qualifications" | "responsibilities", index: number) => {
    setSelectedJob((prev: any) => ({
      ...prev,
      [field]: Array.isArray(prev[field])
        ? prev[field].filter((_: any, i: number) => i !== index)
        : [],
    }));
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSelectChange = (name: string, value: string) =>
    setFormData({ ...formData, [name]: value });

  const handleArrayChange = (
    field: "qualifications" | "responsibilities",
    index: number,
    value: string
  ) => {
    const newArray = [...formData[field]];
    newArray[index] = value;
    setFormData({ ...formData, [field]: newArray });
  };

  const addArrayItem = (field: "qualifications" | "responsibilities") =>
    setFormData({ ...formData, [field]: [...formData[field], ""] });

  const removeArrayItem = (field: "qualifications" | "responsibilities", index: number) => {
    const newArray = formData[field].filter((_, i) => i !== index);
    setFormData({ ...formData, [field]: newArray });
  };

  return (
    <>
      <TaxonomyManagerDialog
        section={managing}
        taxonomy={taxonomy}
        onClose={() => setManaging(null)}
        onSave={saveTaxonomy}
      />

      {/* Create Form */}
      <Card className="mb-8">
        <CardHeader>
          <CardTitle>Add New Career Opportunity</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Consultant Position & Thematic Area */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="flex items-center justify-between">
                  <Label>Consultant Position *</Label>
                  <ManageOptionsButton label="Add or edit consultant positions" onClick={() => setManaging("consultantPositions")} />
                </div>
                <Select
                  value={formData.title}
                  onValueChange={handleTitleChange}
                >
                  <SelectTrigger className="cursor-pointer">
                    <SelectValue placeholder="Select Consultant Position" />
                  </SelectTrigger>
                  <SelectContent>
                    {withCurrent(POSITION_NAMES, formData.title).map((title) => (
                      <SelectItem key={title} value={title} className="cursor-pointer">
                        {title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Thematic Area - Conditionally Rendered */}
              {shouldShowAdditionalFields() && (
                <div>
                  <Label>Thematic Area *</Label>
                  <Select
                    value={formData.department}
                    onValueChange={handleDepartmentChange}
                  >
                    <SelectTrigger className="cursor-pointer">
                      <SelectValue placeholder="Select Thematic Area" />
                    </SelectTrigger>
                    <SelectContent>
                      {DEPARTMENTS.map((dep) => (
                        <SelectItem key={dep} value={dep}>{dep}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {/* Sub-Thematic Area - Conditionally Rendered */}
            {shouldShowAdditionalFields() && (
              <div>
                <Label>Sub-Thematic Area *</Label>
                <Select
                  value={formData.subThematicArea}
                  onValueChange={(v) => handleSelectChange("subThematicArea", v)}
                  disabled={!formData.department}
                >
                  <SelectTrigger className="cursor-pointer">
                    <SelectValue
                      placeholder={
                        formData.department
                          ? "Select Sub-Thematic Area"
                          : "Please select Thematic Area first"
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {formData.department && SUB_THEMATIC_AREAS[formData.department]?.map((subArea, index) => (
                      <SelectItem key={index} value={subArea}>
                        {subArea}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Type of Assignment & Sub-Assignment - Conditionally Rendered */}
            {shouldShowAdditionalFields() && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Type of Assignment *</Label>
                  <Select
                    value={formData.assignmentType}
                    onValueChange={handleAssignmentTypeChange}
                  >
                    <SelectTrigger className="cursor-pointer">
                      <SelectValue placeholder="Select Type of Assignment" />
                    </SelectTrigger>
                    <SelectContent>
                      {ASSIGNMENT_TYPES.map((type) => (
                        <SelectItem key={type} value={type}>{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Sub-Assignment Type *</Label>
                  <Select
                    value={formData.subAssignmentType}
                    onValueChange={(v) => handleSelectChange("subAssignmentType", v)}
                    disabled={!formData.assignmentType}
                  >
                    <SelectTrigger className="cursor-pointer">
                      <SelectValue
                        placeholder={
                          formData.assignmentType
                            ? "Select Sub-Assignment Type"
                            : "Please select Type of Assignment first"
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {formData.assignmentType && SUB_ASSIGNMENT_TYPES[formData.assignmentType]?.map((subType, index) => (
                        <SelectItem key={index} value={subType}>
                          {subType}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {/* Location + Type */}
            {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Location *</Label>
                <Input
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  placeholder="Kathmandu, Nepal"
                  required
                />
              </div>
              <div>
                <Label>Employment Type *</Label>
                <Select
                  value={formData.type}
                  onValueChange={(v) => handleSelectChange("type", v)}
                >
                  <SelectTrigger className="cursor-pointer">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {EMPLOYMENT_TYPES.map((type) => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div> */}

            {/* Status */}
            <div>
              <Label>Status *</Label>
              <Select
                value={formData.status}
                onValueChange={(v) => handleSelectChange("status", v)}
              >
                <SelectTrigger className="cursor-pointer">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_TYPES.map((status) => (
                    <SelectItem key={status} value={status}>{status}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Description */}
            {/* <div>
              <Label>Job Description *</Label>
              <Textarea
                name="description"
                rows={4}
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Enter detailed job description..."
                required
              />
            </div> */}

            {/* Education */}
            <div>
              <Label>Minimum Academic Qualification *</Label>
              <Input
                name="education"
                value={formData.education}
                onChange={handleInputChange}
                placeholder="e.g., Bachelor's degree"
                required
              />
            </div>

            {/* Qualifications */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <Label>Minimum Experience</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="cursor-pointer"
                  onClick={() => addArrayItem("qualifications")}
                >
                  <Plus className="w-4 h-4 mr-1" /> Add
                </Button>
              </div>
              {formData.qualifications.map((q, i) => (
                <div key={i} className="flex items-center gap-2 mb-2">
                  <Input
                    value={q}
                    onChange={(e) => handleArrayChange("qualifications", i, e.target.value)}
                    placeholder={`Qualification ${i + 1}`}
                  />
                  {formData.qualifications.length > 1 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="cursor-pointer"
                      onClick={() => removeArrayItem("qualifications", i)}
                    >
                      <Trash className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>

            {/* Responsibilities */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <Label>Key Responsibilities</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="cursor-pointer"
                  onClick={() => addArrayItem("responsibilities")}
                >
                  <Plus className="w-4 h-4 mr-1" /> Add
                </Button>
              </div>
              {formData.responsibilities.map((r, i) => (
                <div key={i} className="flex items-center gap-2 mb-2">
                  <Input
                    value={r}
                    onChange={(e) => handleArrayChange("responsibilities", i, e.target.value)}
                    placeholder={`Responsibility ${i + 1}`}
                  />
                  {formData.responsibilities.length > 1 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="cursor-pointer"
                      onClick={() => removeArrayItem("responsibilities", i)}
                    >
                      <Trash className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>

            <Button type="submit" className="w-full cursor-pointer">
              Add Career Opportunity
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Jobs Table */}
      <Card>
        <CardHeader>
          <CardTitle>All Career Opportunities</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="mt-6 overflow-x-auto">
            <table className="w-full border border-gray-200 text-sm">
              <thead className="bg-gray-100">
                <tr>
                  <th className="p-2 text-left">#</th>
                  <th className="p-2 text-left">Consultant Position</th>
                  <th className="p-2 text-left">Thematic Area</th>
                  <th className="p-2 text-left">Sub-Thematic Area</th>
                  <th className="p-2 text-left">Assignment Type</th>
                  <th className="p-2 text-left">Sub-Assignment Type</th>
                  {/* <th className="p-2 text-left">Location</th> */}
                  {/* <th className="p-2 text-left">Type</th> */}
                  <th className="p-2 text-left">Status</th>
                  <th className="p-2 text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={10} className="p-4 text-center">Loading...</td>
                  </tr>
                ) : jobs.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="p-4 text-center">No jobs found</td>
                  </tr>
                ) : (
                  jobs.map((job, index) => (
                    <tr key={job.id} className="border-t hover:bg-gray-50">
                      <td className="p-2">{index + 1}</td>
                      <td className="p-2">{job.title}</td>
                      <td className="p-2">{job.department}</td>
                      <td className="p-2">{job.subThematicArea || "-"}</td>
                      <td className="p-2">{job.assignmentType || "-"}</td>
                      <td className="p-2">{job.subAssignmentType || "-"}</td>
                      {/* <td className="p-2">{job.location}</td> */}
                      {/* <td className="p-2">{job.type}</td> */}
                      <td className="p-2">{job.status}</td>
                      <td className="p-2 flex justify-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleView(job.id)}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(job.id)}
                        >
                          <Trash className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* View / Edit Dialog */}
      {selectedJob && (
        <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
          <DialogContent className="w-full sm:max-w-2xl max-h-[90vh] flex flex-col h-full">
            <DialogHeader>
              <DialogTitle>{isEditing ? "Edit Job" : "View Job"}</DialogTitle>
            </DialogHeader>

            <div className="flex-1 overflow-y-auto space-y-4 mt-4 pr-1">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="flex items-center justify-between">
                    <Label>Consultant Position</Label>
                    {isEditing && (
                      <ManageOptionsButton label="Add or edit consultant positions" onClick={() => setManaging("consultantPositions")} />
                    )}
                  </div>
                  <Select
                    value={selectedJob.title}
                    onValueChange={(v) => handleJobFieldChange("title", v)}
                    disabled={!isEditing}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select Consultant Position" />
                    </SelectTrigger>
                    <SelectContent>
                      {withCurrent(POSITION_NAMES, selectedJob.title).map((title) => (
                        <SelectItem key={title} value={title}>{title}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Thematic Area in Edit Dialog - Conditionally Rendered */}
                {shouldShowAdditionalFieldsEdit() && (
                  <div>
                    <Label>Department</Label>
                    <Select
                      value={selectedJob.department}
                      onValueChange={(v) => handleJobFieldChange("department", v)}
                      disabled={!isEditing}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select department" />
                      </SelectTrigger>
                      <SelectContent>
                        {withCurrent(DEPARTMENTS, selectedJob.department).map((dep) => (
                          <SelectItem key={dep} value={dep}>{dep}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              {/* Sub-Thematic Area in Edit Dialog - Conditionally Rendered */}
              {shouldShowAdditionalFieldsEdit() && (
                <div>
                  <Label>Sub-Thematic Area</Label>
                  <Select
                    value={selectedJob.subThematicArea}
                    onValueChange={(v) => handleJobFieldChange("subThematicArea", v)}
                    disabled={!isEditing || !selectedJob.department}
                  >
                    <SelectTrigger>
                      <SelectValue
                        placeholder={
                          selectedJob.department
                            ? "Select Sub-Thematic Area"
                            : "Please select Thematic Area first"
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {selectedJob.department && withCurrent(SUB_THEMATIC_AREAS[selectedJob.department] || [], selectedJob.subThematicArea).map((subArea, index) => (
                        <SelectItem key={index} value={subArea}>
                          {subArea}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Type of Assignment & Sub-Assignment in Edit Dialog - Conditionally Rendered */}
              {shouldShowAdditionalFieldsEdit() && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Type of Assignment</Label>
                    <Select
                      value={selectedJob.assignmentType}
                      onValueChange={(v) => handleJobFieldChange("assignmentType", v)}
                      disabled={!isEditing}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select Type of Assignment" />
                      </SelectTrigger>
                      <SelectContent>
                        {withCurrent(ASSIGNMENT_TYPES, selectedJob.assignmentType).map((type) => (
                          <SelectItem key={type} value={type}>{type}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Sub-Assignment Type</Label>
                    <Select
                      value={selectedJob.subAssignmentType}
                      onValueChange={(v) => handleJobFieldChange("subAssignmentType", v)}
                      disabled={!isEditing || !selectedJob.assignmentType}
                    >
                      <SelectTrigger>
                        <SelectValue
                          placeholder={
                            selectedJob.assignmentType
                              ? "Select Sub-Assignment Type"
                              : "Please select Type of Assignment first"
                          }
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {selectedJob.assignmentType && withCurrent(SUB_ASSIGNMENT_TYPES[selectedJob.assignmentType] || [], selectedJob.subAssignmentType).map((subType, index) => (
                          <SelectItem key={index} value={subType}>
                            {subType}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-4"> */}
              {/* <div>
                  <Label>Location</Label>
                  <Input
                    value={selectedJob.location}
                    onChange={(e) => handleJobFieldChange("location", e.target.value)}
                    disabled={!isEditing}
                  />
                </div> */}
              {/* <div>
                  <Label>Type</Label>
                  <Select
                    value={selectedJob.type}
                    onValueChange={(v) => handleJobFieldChange("type", v)}
                    disabled={!isEditing}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      {EMPLOYMENT_TYPES.map((type) => (
                        <SelectItem key={type} value={type}>{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div> */}
              {/* </div> */}

              <div>
                <Label>Status</Label>
                <Select
                  value={selectedJob.status}
                  onValueChange={(v) => handleJobFieldChange("status", v)}
                  disabled={!isEditing}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_TYPES.map((status) => (
                      <SelectItem key={status} value={status}>{status}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Minimum Academic Qualification</Label>
                <Input
                  value={selectedJob.education}
                  onChange={(e) => handleJobFieldChange("education", e.target.value)}
                  disabled={!isEditing}
                />
              </div>

              {/* Qualifications */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <Label>Minimum Experience</Label>
                  {isEditing && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => addArrayField("qualifications")}
                    >
                      <Plus className="w-4 h-4 mr-1" /> Add
                    </Button>
                  )}
                </div>
                {selectedJob.qualifications.map((q: string, i: number) => (
                  <div key={i} className="flex items-center gap-2 mb-2">
                    <Input
                      value={q}
                      onChange={(e) => handleArrayFieldChange("qualifications", i, e.target.value)}
                      disabled={!isEditing}
                    />
                    {isEditing && selectedJob.qualifications.length > 1 && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => removeArrayField("qualifications", i)}
                      >
                        <Trash className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>

              {/* Responsibilities */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <Label>Key Responsibilities</Label>
                  {isEditing && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => addArrayField("responsibilities")}
                    >
                      <Plus className="w-4 h-4 mr-1" /> Add
                    </Button>
                  )}
                </div>
                {selectedJob.responsibilities.map((r: string, i: number) => (
                  <div key={i} className="flex items-center gap-2 mb-2">
                    <Input
                      value={r}
                      onChange={(e) => handleArrayFieldChange("responsibilities", i, e.target.value)}
                      disabled={!isEditing}
                    />
                    {isEditing && selectedJob.responsibilities.length > 1 && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => removeArrayField("responsibilities", i)}
                      >
                        <Trash className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-2 mt-4">
                {!isEditing && (
                  <Button onClick={() => setIsEditing(true)}>
                    <Pencil className="w-4 h-4 mr-1" /> Edit
                  </Button>
                )}
                {isEditing && (
                  <Button onClick={handleUpdate}>
                    <Save className="w-4 h-4 mr-1" /> Save
                  </Button>
                )}
                <Button variant="outline" onClick={() => setViewDialogOpen(false)}>
                  Close
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
